import WDUtil from '../wd-util';
import { WDModel } from '../wd-model';


/**
 * Simple fetch class backed by WDModel with clean Promise-based API
 * 
 * Usage (just like original WDFetch):
 *   const api = new WDFetch(url);
 *   api.name = 'John';
 *   api.email = 'john@example.com';
 *   api.req('POST').then(result => {}).catch(err => {}).finally(() => {});
 * 
 * Key improvements over WDFetch:
 * - Uses WDModel instead of WDProxy for better performance
 * - Cleaner error handling with structured error information
 * - Simplified code without complex state management
 * - Clean Promise-based API (no hooks/callbacks required)
 */

var WDFetchCounter = 0;

export default class WDFetch extends WDModel
{
  __controller = null;
  __uuid = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

  __config = {
    contentType: 'application/json',
    auth: {},
    reqURL: null,
    reqURI: null,
    allowMultipleRequests: true,
    headers: {}
  };

  constructor(config, values)
  {
    super(values);

    // Handle config parameter
    if (typeof config === 'string')
    {
      this.__config.reqURI = config;
    } else if (typeof config === 'object')
    {
      WDUtil.merge(this.__config, config);
    }
  }

  get canFetch()
  {
    return (this.__config?.reqURI || '').length > 0;
  };

  // Get unique instance identifier
  getUUID()
  {
    return this.__uuid;
  }

  // Configuration methods (matching original WDFetch API)
  getConfig()
  {
    return this.__config;
  }

  getReqURI()
  {
    return this.__config?.reqURI || '';
  }

  setReqURI(uri)
  {
    this.__config.reqURI = uri;
    return this;
  }

  getReqURL()
  {
    return this.__config.reqURL || '';
  }

  setReqURL(url)
  {
    this.__config.reqURL = url;
    return this;
  }

  // Request cancellation
  abort()
  {
    if (this.__controller)
    {
      this.__controller.abort('Request cancelled');
      this.__controller = null;
    }
    return this;
  }

  // URL building (matching original WDFetch)
  buildURI(addURI)
  {
    let uri = this.__config.reqURI;
    if (addURI) uri += addURI;
    return uri;
  }

  buildURL(uri)
  {
    const url = String(this.getReqURL() + this.buildURI(uri)).trim();

    try
    {
      return new URL(url);
    } catch (err)
    {
      if (WDConfig?.debug?.fetch) console.log('Error forming URL', url, err);
      throw new Error(`Invalid URL: ${url}`);
    }
  }

  // Header building (matching original WDFetch)
  buildHeaders()
  {
    const headers = {
      'Content-Type': this.__config.contentType,
      'X-Requested-With': 'XMLHttpRequest'
    };

    this.buildAuth();
    Object.assign(headers, this.__config.headers);

    return new Headers(headers);
  }

  // Authentication (matching original WDFetch)
  buildAuth()
  {
    if (this.__config.auth)
    {
      const auth = this.__config.auth;

      if (auth.mode === 'basic')
      {
        this.__config.headers.Authorization = 'Basic ' + btoa(auth.login + ':' + auth.password);
      } else if (auth.mode === 'digest')
      {
        // Future implementation
      }
    }
  }

  // Request building (similar to original WDFetch)
  buildRequest(method, uri)
  {
    // Setup controller for aborts
    this.__controller = (this.__config.allowMultipleRequests === false) ? new AbortController() : null;

    // Create URL
    const url = this.buildURL(uri);

    if (typeof method === 'undefined') method = 'GET';

    // Build options
    const options = this.buildOptions(method, url);

    return new Request(url, options);
  }

  // Options building (similar to original WDFetch)
  buildOptions(method, url)
  {
    const options = {
      method: method,
      headers: this.buildHeaders()
    };

    if (this.__controller !== null) options.signal = this.__controller.signal;
    if (this.__config.auth !== false) options.credentials = 'include';

    const dataObj = this.toObject();
    const dataKeys = Object.keys(dataObj);

    if (dataKeys.length > 0)
    {
      // Handle different methods
      if (method === 'PUT' && dataObj.file)
      {
        options.body = dataObj.file;
      } else if (method === 'GET')
      {
        dataKeys.forEach(key => url.searchParams.append(key, dataObj[key]));
      } else
      {
        options.body = JSON.stringify(dataObj);
      }
    }

    return options;
  }

  // Content type detection (matching original WDFetch)
  buildResponseContentType(response)
  {
    const ct = response.headers.get("content-type");
    const pos = ct?.indexOf(';');
    return (pos !== -1) ? ct.substr(0, pos) : ct;
  }

  // Main request method - returns Promise (matching original WDFetch API)
  req(method, uri)
  {
    if (WDConfig?.debug?.fetch)
    {
      //console.log('REQ CONFIG',this.getUUID());
      console.log('Running ' + method + ' request', this.__config.reqURI, uri);
      console.log('Request Data', this.toObject());
      //console.trace();
      //if (this.__config.reqURI == '/creditcards/accounts') console.trace('/creditcards/accounts');
    }

    // Cancel any current request
    if (this.__controller) this.abort();

    // Build the request
    const request = this.buildRequest(method, uri);

    // Execute fetch and return Promise
    return fetch(request)
      .then(response =>
      {
        const contentType = this.buildResponseContentType(response);

        if (response.ok)
        {
          // Success - process response based on expected content type
          if (contentType === this.__config.contentType)
          {
            return (contentType === 'application/json') ? response.json() : response.text();
          } else
          {
            // Content type mismatch - usually server parse error
            const error = new Error(response.message || 'Content type mismatch');
            error.name = 'ContentTypeError';
            error.code = response.status;
            error.expectedContentType = this.__config.contentType;
            error.actualContentType = contentType;
            error.response = response;
            throw error;
          }
        } else
        {
          // HTTP error - create structured error
          const error = new Error(response.statusText || `HTTP ${response.status}`);
          error.name = 'HttpError';
          error.code = response.status;
          error.statusText = response.statusText;
          error.contentType = contentType;
          error.response = response;
          throw error;
        }
      })
      .catch(err =>
      {
        if (WDConfig?.debug?.fetch)
        {
          console.log('Error On Request', this.__config.reqURI, err);
          //console.trace();
        }

        // Handle different error types with clean error info
        if (err.name === 'AbortError')
        {
          const error = new Error('Request was cancelled');
          error.name = 'AbortError';
          error.cancelled = true;
          throw error;
        } else if (err.code === 401)
        {
          const error = new Error('Unauthorized');
          error.name = 'UnauthorizedError';
          error.code = 401;
          throw error;
        } else if (err.code === 403)
        {
          const error = new Error('Forbidden');
          error.name = 'ForbiddenError';
          error.code = 403;
          throw error;
        } else if (err.name === 'HttpError' || err.name === 'ContentTypeError')
        {
          // Already processed - re-throw
          throw err;
        } else
        {
          // Network or other errors
          const error = new Error(err.message || 'Network error');
          error.name = 'NetworkError';
          error.originalError = err;
          throw error;
        }
      })
      .finally(() =>
      {
        this.__controller = null;
      });
  }

  // HTTP method shortcuts (matching original WDFetch)
  post(uri)
  {
    return this.req('POST', uri);
  }

  put(uri)
  {
    return this.req('PUT', uri);
  }

  del(uri)
  {
    return this.req('DELETE', uri);
  }

  // Backwards compatibility
  add(obj)
  {
    return this.merge(obj);
  }

  /**
   * Cleanup method to abort pending requests and clear resources
   */
  destroy()
  {
    console.trace('WDFetch Destroy Called', this.toObject());
    // Abort any pending requests
    this.abort();

    // Clear the controller reference
    this.__controller = null;

    // Clear configuration
    this.__config = null;

    // Call parent destroy if available
    if (super.destroy)
    {
      super.destroy();
    }

    return this;
  }
}
